import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Bot, Check, ClipboardList, Lock, MessageSquare, Send, Sparkles } from 'lucide-react';
import type { PrepareRequestResult, Visibility } from '@vwt/contracts';
import { Badge, Button, Card, Checkbox, cn, Field, Input, NativeSelect, Tabs, TabsContent, TabsList, TabsTrigger, Textarea, toast } from '@vwt/ui';
import { api, isApiError, newIdempotencyKey, useApiQuery } from '../../lib/api.js';
import { fieldErrors, toastError } from '../../lib/errors.js';
import { currentLocale, useTranslation } from '../../lib/i18n.js';
import { PageHeader } from '../../components/common.js';
import { KeyLabel, RequestStatusBadge } from '../../components/badges.js';
import { SaveTemplateDialog, TemplatePicker } from '../../components/templates.js';
import { MarkdownEditor, MarkdownView } from '../../components/markdown.js';

interface Form {
  title: string;
  problemMarkdown: string;
  currentBehavior: string;
  expectedOutcome: string;
  impact: string;
  impactUnknown: boolean;
  reproduction: string;
  affectedArea: string;
  triageTeamId: string;
  visibility: Visibility;
  originalLocale: string;
}

const empty = (): Form => ({
  title: '',
  problemMarkdown: '',
  currentBehavior: '',
  expectedOutcome: '',
  impact: '',
  impactUnknown: false,
  reproduction: '',
  affectedArea: '',
  triageTeamId: '',
  visibility: 'COMPANY',
  originalLocale: currentLocale(),
});

/**
 * Request intake (Section 6.2/14.1): conversational and form tabs over the same saved draft.
 * The structured draft is the only memory; no transcript is stored. Drafts autosave and resume.
 */
export function IntakePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const draftId = params.get('draft');
  const [form, setForm] = useState<Form>(empty);
  const [state, setState] = useState<PrepareRequestResult | null>(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [step, setStep] = useState<'edit' | 'review'>('edit');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const dirty = useRef(false);
  const teams = useApiQuery('listTeams', { query: {} });

  // Resume an existing draft.
  useEffect(() => {
    if (!draftId || state) return;
    void api.call('getRequest', { params: { id: draftId } }).then((r) => {
      const d = r!.data;
      setForm({
        title: d.title,
        problemMarkdown: d.problemMarkdown,
        currentBehavior: d.currentBehavior ?? '',
        expectedOutcome: d.expectedOutcome ?? '',
        impact: d.impact ?? '',
        impactUnknown: d.impactUnknown,
        reproduction: d.reproduction ?? '',
        affectedArea: d.affectedArea ?? '',
        triageTeamId: d.triageTeam?.id ?? '',
        visibility: d.visibility,
        originalLocale: d.originalLocale === 'und' ? currentLocale() : d.originalLocale,
      });
      setState({ draft: d, missingFields: [], duplicateCandidates: [] });
    });
  }, [draftId, state]);

  const save = async (): Promise<PrepareRequestResult | null> => {
    setSaving(true);
    try {
      const body = {
        ...(state ? { draftId: state.draft.id, expectedVersion: state.draft.version } : {}),
        title: form.title,
        problemMarkdown: form.problemMarkdown,
        currentBehavior: form.currentBehavior,
        expectedOutcome: form.expectedOutcome,
        ...(form.impactUnknown ? { impactUnknown: true } : { impact: form.impact }),
        reproduction: form.reproduction,
        affectedArea: form.affectedArea,
        originalLocale: form.originalLocale,
        visibility: form.visibility,
        ...(form.triageTeamId ? { triageTeamId: form.triageTeamId } : {}),
      };
      const result = await api.call('prepareRequest', { body, idempotencyKey: newIdempotencyKey() });
      setState(result!.data);
      setErrors({});
      dirty.current = false;
      if (!draftId) setParams((p) => (p.set('draft', result!.data.draft.id), p), { replace: true });
      return result!.data;
    } catch (error) {
      if (isApiError(error) && error.code === 'VERSION_CONFLICT') {
        const fresh = await api.call('getRequest', { params: { id: state!.draft.id } });
        setState((s) => (s ? { ...s, draft: fresh!.data } : s));
      }
      setErrors(fieldErrors(error));
      toastError(error);
      return null;
    } finally {
      setSaving(false);
    }
  };

  // Autosave after a pause in typing.
  useEffect(() => {
    if (!dirty.current || !form.title.trim()) return;
    const id = setTimeout(() => void save(), 1200);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const update = (patch: Partial<Form>) => {
    dirty.current = true;
    setForm((f) => ({ ...f, ...patch }));
  };

  const submit = async () => {
    const saved = dirty.current || !state ? await save() : state;
    if (!saved) return;
    if (saved.missingFields.length) {
      toast.error(t('intake.missing', { fields: saved.missingFields.join(', ') }));
      setStep('edit');
      return;
    }
    try {
      const result = await api.call('submitRequest', { body: { draftId: saved.draft.id, expectedVersion: saved.draft.version }, idempotencyKey: newIdempotencyKey() });
      toast.success(t('intake.submitted', { key: result!.data.key }));
      navigate(`/requests/${result!.data.id}`);
    } catch (error) {
      toastError(error);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader title={t('intake.title')} subtitle={t('intake.subtitle')} actions={<span className="flex items-center gap-1.5 text-xs text-fg-subtle"><Lock className="size-3" />{t('intake.draftNote')}{saving ? ` · ${t('common.saving')}` : state ? ` · ${t('intake.saved')}` : ''}</span>} />
      <div className="mx-auto grid w-full max-w-6xl flex-1 grid-cols-1 gap-6 p-4 md:p-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0">
          {step === 'edit' ? (
            <Tabs defaultValue={params.get('mode') === 'form' ? 'form' : 'assistant'}>
              <TabsList>
                <TabsTrigger value="assistant">
                  <Sparkles /> {t('intake.tabs.assistant')}
                </TabsTrigger>
                <TabsTrigger value="form">
                  <ClipboardList /> {t('intake.tabs.form')}
                </TabsTrigger>
              </TabsList>
              <TabsContent value="assistant" className="pt-4">
                <Assistant
                  draftId={state?.draft.id}
                  draftVersion={state?.draft.version}
                  onDraft={async (id) => {
                    const fresh = await api.call('getRequest', { params: { id } });
                    const d = fresh!.data;
                    setState((s) => ({ draft: d, missingFields: s?.missingFields ?? [], duplicateCandidates: s?.duplicateCandidates ?? [] }));
                    setForm((f) => ({ ...f, title: d.title, problemMarkdown: d.problemMarkdown, currentBehavior: d.currentBehavior ?? '', expectedOutcome: d.expectedOutcome ?? '', impact: d.impact ?? '', impactUnknown: d.impactUnknown, reproduction: d.reproduction ?? '', affectedArea: d.affectedArea ?? '' }));
                    if (!draftId) setParams((p) => (p.set('draft', id), p), { replace: true });
                  }}
                  onReview={() => setStep('review')}
                />
              </TabsContent>
              <TabsContent value="form" className="pt-4">
                <form
                  className="flex flex-col gap-4"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void save().then((r) => r && setStep('review'));
                  }}
                >
                  <TemplatePicker
                    kind="REQUEST"
                    onApply={(p) => update(Object.fromEntries(Object.entries(p).filter(([, v]) => v !== undefined)))}
                  />
                  <Field label={t('intake.fields.title')} htmlFor="req-title" required error={errors.title}>
                    <Input id="req-title" value={form.title} onChange={(e) => update({ title: e.target.value })} placeholder={t('intake.fields.titlePlaceholder')} maxLength={240} autoFocus />
                  </Field>
                  <Field label={t('intake.fields.problem')} required error={errors.problemMarkdown}>
                    <MarkdownEditor value={form.problemMarkdown} onChange={(v) => update({ problemMarkdown: v })} placeholder={t('intake.fields.problemPlaceholder')} />
                  </Field>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field label={t('intake.fields.currentBehavior')} htmlFor="req-current">
                      <Textarea id="req-current" value={form.currentBehavior} onChange={(e) => update({ currentBehavior: e.target.value })} rows={3} />
                    </Field>
                    <Field label={t('intake.fields.expectedOutcome')} htmlFor="req-expected">
                      <Textarea id="req-expected" value={form.expectedOutcome} onChange={(e) => update({ expectedOutcome: e.target.value })} rows={3} />
                    </Field>
                  </div>
                  <Field label={t('intake.fields.impact')} htmlFor="req-impact">
                    <Textarea id="req-impact" value={form.impact} onChange={(e) => update({ impact: e.target.value, impactUnknown: false })} placeholder={t('intake.fields.impactPlaceholder')} rows={2} disabled={form.impactUnknown} />
                    <label className="flex items-center gap-2 text-sm text-fg-muted">
                      <Checkbox checked={form.impactUnknown} onCheckedChange={(v) => update({ impactUnknown: v === true })} /> {t('intake.fields.impactUnknown')}
                    </label>
                  </Field>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field label={t('intake.fields.reproduction')} htmlFor="req-repro">
                      <Textarea id="req-repro" value={form.reproduction} onChange={(e) => update({ reproduction: e.target.value })} rows={3} />
                    </Field>
                    <Field label={t('intake.fields.affectedArea')} htmlFor="req-area">
                      <Input id="req-area" value={form.affectedArea} onChange={(e) => update({ affectedArea: e.target.value })} />
                    </Field>
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <Field label={t('intake.fields.team')} htmlFor="req-team">
                      <NativeSelect id="req-team" value={form.triageTeamId} onChange={(e) => update({ triageTeamId: e.target.value })}>
                        <option value="">{t('intake.fields.teamAuto')}</option>
                        {(teams.data?.data ?? []).map((team) => (
                          <option key={team.id} value={team.id}>
                            {team.name}
                          </option>
                        ))}
                      </NativeSelect>
                    </Field>
                    <Field label={t('intake.fields.visibility')} htmlFor="req-vis" hint={t(`status.visibilityHelp.${form.visibility}`)}>
                      <NativeSelect id="req-vis" value={form.visibility} onChange={(e) => update({ visibility: e.target.value as Visibility })}>
                        <option value="COMPANY">{t('status.visibility.COMPANY')}</option>
                        <option value="RESTRICTED">{t('status.visibility.RESTRICTED')}</option>
                      </NativeSelect>
                    </Field>
                    <Field label={t('intake.fields.language')} htmlFor="req-lang">
                      <NativeSelect id="req-lang" value={form.originalLocale} onChange={(e) => update({ originalLocale: e.target.value })}>
                        <option value="en">English</option>
                        <option value="pt-BR">Português</option>
                        <option value="es">Español</option>
                        <option value="und">{t('common.unknown')}</option>
                      </NativeSelect>
                    </Field>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="ghost" onClick={() => setSavingTemplate(true)} disabled={!form.title.trim() && !form.problemMarkdown.trim()}>
                      {t('templates.saveAs')}
                    </Button>
                    <Button type="submit" variant="primary" disabled={!form.title.trim()} loading={saving}>
                      {t('intake.review')}
                    </Button>
                  </div>
                </form>
                {savingTemplate ? (
                  <SaveTemplateDialog
                    kind="REQUEST"
                    defaultName={form.title.trim()}
                    onClose={() => setSavingTemplate(false)}
                    payload={{
                      ...(form.title.trim() ? { title: form.title.trim() } : {}),
                      ...(form.problemMarkdown.trim() ? { problemMarkdown: form.problemMarkdown } : {}),
                      ...(form.currentBehavior.trim() ? { currentBehavior: form.currentBehavior } : {}),
                      ...(form.expectedOutcome.trim() ? { expectedOutcome: form.expectedOutcome } : {}),
                      ...(form.reproduction.trim() ? { reproduction: form.reproduction } : {}),
                      ...(form.affectedArea.trim() ? { affectedArea: form.affectedArea } : {}),
                    }}
                  />
                ) : null}
              </TabsContent>
            </Tabs>
          ) : (
            <Review form={form} state={state} onBack={() => setStep('edit')} onSubmit={() => void submit()} />
          )}
        </div>
        <aside className="flex flex-col gap-4">
          {state && state.missingFields.length ? (
            <Card className="px-4 py-3 text-sm">
              <p className="font-medium">{t('intake.missing', { fields: state.missingFields.map((f) => t(`intake.fields.${f === 'problemMarkdown' ? 'problem' : f}` as 'intake.fields.title', { defaultValue: f })).join(', ') })}</p>
            </Card>
          ) : null}
          <Card className="px-4 py-3">
            <h2 className="text-sm font-semibold">{t('intake.duplicates')}</h2>
            <p className="mt-0.5 text-xs text-fg-subtle">{t('intake.duplicatesHelp')}</p>
            <ul className="mt-2 flex flex-col gap-1.5">
              {(state?.duplicateCandidates ?? []).map((d) => (
                <li key={d.id}>
                  <a href={`/requests/${d.id}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded px-1 py-1 text-sm hover:bg-muted">
                    <KeyLabel value={d.key} />
                    <span className="min-w-0 flex-1 truncate">{d.title}</span>
                    <RequestStatusBadge status={d.status} />
                  </a>
                </li>
              ))}
              {state && !state.duplicateCandidates.length ? <li className="text-xs text-fg-subtle">{t('common.noResults')}</li> : null}
            </ul>
          </Card>
        </aside>
      </div>
    </div>
  );
}

function Review({ form, state, onBack, onSubmit }: { form: Form; state: PrepareRequestResult | null; onBack: () => void; onSubmit: () => void }) {
  const { t } = useTranslation();
  const rows: Array<[string, string]> = [
    [t('intake.fields.problem'), form.problemMarkdown],
    [t('intake.fields.currentBehavior'), form.currentBehavior],
    [t('intake.fields.expectedOutcome'), form.expectedOutcome],
    [t('intake.fields.impact'), form.impactUnknown ? t('intake.fields.impactUnknown') : form.impact],
    [t('intake.fields.reproduction'), form.reproduction],
    [t('intake.fields.affectedArea'), form.affectedArea],
  ];
  return (
    <Card className="flex flex-col gap-4 px-5 py-4">
      <div>
        <h2 className="text-base font-semibold">{t('intake.review')}</h2>
        <p className="text-sm text-fg-muted">{t('intake.reviewHelp')}</p>
      </div>
      <h3 className="text-lg font-semibold">{form.title}</h3>
      <dl className="flex flex-col gap-3">
        {rows
          .filter(([, v]) => v.trim())
          .map(([label, value]) => (
            <div key={label}>
              <dt className="text-xs font-semibold text-fg-subtle uppercase">{label}</dt>
              <dd>
                <MarkdownView source={value} />
              </dd>
            </div>
          ))}
      </dl>
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone="neutral">{t(`status.visibility.${form.visibility}`)}</Badge>
        {state?.missingFields.length ? <Badge tone="warning">{t('intake.missing', { fields: state.missingFields.join(', ') })}</Badge> : <Badge tone="success"><Check /> OK</Badge>}
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onBack}>
          {t('common.back')}
        </Button>
        <Button variant="primary" onClick={onSubmit} disabled={!!state?.missingFields.length}>
          <Send /> {t('actions.request.submit')}
        </Button>
      </div>
    </Card>
  );
}

interface Turn {
  role: 'assistant' | 'user';
  text: string;
}

/** Conversational tab: each answer updates the saved draft; the transcript lives only in this tab. */
function Assistant({ draftId, draftVersion, onDraft, onReview }: { draftId: string | undefined; draftVersion: number | undefined; onDraft: (id: string) => Promise<void>; onReview: () => void }) {
  const { t } = useTranslation();
  const [turns, setTurns] = useState<Turn[]>([{ role: 'assistant', text: t('intake.assistantIntro') }]);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [ready, setReady] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // scrollIntoView returns a Promise in current Chromium; effects must not return it.
    void endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [turns]);

  const send = async () => {
    const text = message.trim();
    if (!text || busy) return;
    setTurns((list) => [...list, { role: 'user', text }]);
    setMessage('');
    setBusy(true);
    try {
      const result = await api.call('intakeTurn', { body: { message: text, locale: currentLocale() as 'en', ...(draftId ? { draftId, expectedVersion: draftVersion } : {}) }, idempotencyKey: newIdempotencyKey() });
      const r = result!.data;
      setTurns((list) => [...list, { role: 'assistant', text: r.assistantMessage }]);
      setReady(r.readyForReview);
      if (!r.aiAvailable) setUnavailable(true);
      await onDraft(r.draftId);
    } catch (error) {
      if (isApiError(error) && (error.code === 'CAPABILITY_UNAVAILABLE' || error.code === 'CONNECTOR_UNAVAILABLE' || error.code === 'RATE_LIMITED')) {
        setUnavailable(true);
        setTurns((list) => [...list, { role: 'assistant', text: t('intake.assistantUnavailable') }]);
      } else {
        toastError(error);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="flex h-[520px] flex-col">
      <div className="flex-1 space-y-3 overflow-y-auto p-4" aria-live="polite">
        {turns.map((turn, i) => (
          <div key={i} className={cn('flex gap-2', turn.role === 'user' && 'justify-end')}>
            {turn.role === 'assistant' ? (
              <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent-soft-fg">
                <Bot className="size-4" />
              </div>
            ) : null}
            <div className={cn('max-w-[80%] rounded-[var(--radius-lg)] px-3 py-2 text-sm whitespace-pre-wrap', turn.role === 'user' ? 'bg-accent text-accent-fg' : 'bg-muted text-fg')}>{turn.text}</div>
          </div>
        ))}
        {busy ? <p className="text-xs text-fg-subtle">…</p> : null}
        <div ref={endRef} />
      </div>
      {unavailable ? <p className="border-t border-border bg-warning-soft px-4 py-2 text-xs text-warning">{t('intake.assistantUnavailable')}</p> : null}
      <form
        className="flex items-end gap-2 border-t border-border p-3"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <MessageSquare className="mb-2 size-4 text-fg-subtle" />
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={t('intake.assistantPlaceholder')}
          rows={2}
          className="min-h-10 flex-1"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
          aria-label={t('intake.assistantPlaceholder')}
        />
        <Button type="submit" variant="primary" size="icon" aria-label={t('common.submit')} loading={busy}>
          <Send />
        </Button>
        {ready || draftId ? (
          <Button type="button" variant="secondary" onClick={onReview}>
            {t('intake.review')}
          </Button>
        ) : null}
      </form>
    </Card>
  );
}
